import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
} from "react-icons/fa";
import {
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiEye,
  FiLayers,
  FiLock,
  FiShield,
  FiSmartphone,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import { RiSparklingLine } from "react-icons/ri";
import Card from "../ui/Card";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8 },
  },
};

const productPillars = [
  {
    icon: FiLock,
    title: "Security-First Foundation",
    text: "Every account is protected with authenticated access and role-aware boundaries that keep data in the right hands.",
  },
  {
    icon: FiLayers,
    title: "Focused Product UX",
    text: "The interface is deliberately simple so users can capture, search, and act on notes without friction.",
  },
  {
    icon: FiSmartphone,
    title: "Cross-Device Continuity",
    text: "A consistent experience across desktop and mobile keeps your workflow moving wherever you work.",
  },
  {
    icon: FiZap,
    title: "Operational Speed",
    text: "Fast interactions and clear navigation help teams maintain momentum from login through note management.",
  },
];

const productStats = [
  {
    icon: FiUsers,
    value: "Role-aware",
    label: "Access controls for users and admins",
  },
  {
    icon: FiTrendingUp,
    value: "Audit-ready",
    label: "Activity visibility for confidence",
  },
  {
    icon: FiClock,
    value: "Daily-use",
    label: "Built for quick note operations",
  },
];

const productPrinciples = [
  {
    icon: FiEye,
    title: "Clarity over clutter",
    text: "Navigation, actions, and states are designed to be obvious at a glance.",
  },
  {
    icon: FiCheckCircle,
    title: "Trust by default",
    text: "Users can work confidently knowing security is built into core interactions.",
  },
  {
    icon: FiShield,
    title: "Reliability at scale",
    text: "Consistent patterns support teams as usage grows and workflows become critical.",
  },
];

const AboutPage = () => {
  return (
    <div className="relative min-h-[calc(100vh-74px)] overflow-hidden bg-bg-subtle py-10 sm:py-12">
      <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary/6 blur-3xl" />

      <motion.div
        className="mx-auto flex w-[92%] max-w-6xl flex-col gap-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <Card className="relative overflow-hidden border-primary/15 bg-gradient-to-r from-bg-surface via-bg-surface to-bg-subtle px-6 py-8 sm:px-9">
            <div className="absolute right-5 top-5 hidden rounded-full border border-border-subtle bg-bg-surface/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary sm:inline-flex">
              About Incrypt
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr]">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full border border-border-subtle bg-bg-surface px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  <RiSparklingLine className="text-primary" />
                  Productive work, protected by design
                </div>
                <h1 className="text-h1 font-semibold leading-tight text-text-main sm:text-[38px]">
                  Incrypt is the secure notes product for teams that treat information seriously.
                </h1>
                <p className="text-body leading-6 text-text-muted sm:text-[15px]">
                  We built Incrypt to combine enterprise-grade trust with a lightweight user experience. From quick thought capture to structured note management, the product helps professionals move fast without compromising privacy.
                </p>

                <div className="flex flex-wrap gap-3 pt-1">
                  <Link
                    to="/signup"
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150 hover:bg-primary-hover"
                  >
                    Start with Incrypt
                    <FiArrowRight />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 rounded-lg border border-border-subtle bg-bg-surface px-5 py-2.5 text-sm font-semibold text-text-main transition-all duration-150 hover:border-primary/40 hover:text-primary"
                  >
                    Talk to our team
                  </Link>
                </div>
              </div>

              <div className="space-y-3">
                {productStats.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div
                      key={stat.value}
                      className="rounded-lg border border-border-subtle bg-bg-surface/90 p-4"
                    >
                      <div className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Icon className="text-base" />
                      </div>
                      <p className="text-[15px] font-semibold text-text-main">{stat.value}</p>
                      <p className="mt-1 text-[13px] text-text-muted">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {productPillars.map((pillar) => {
            const Icon = pillar.icon;
            return (
              <Card
                key={pillar.title}
                className="group h-full rounded-xl border-border-subtle/90 px-5 py-5 transition-all duration-200 hover:-translate-y-1 hover:border-primary/30 hover:shadow-custom"
              >
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors duration-200 group-hover:bg-primary group-hover:text-white">
                  <Icon className="text-xl" />
                </div>
                <h2 className="text-h3 font-semibold text-text-main">{pillar.title}</h2>
                <p className="mt-2 text-body text-text-muted">{pillar.text}</p>
              </Card>
            );
          })}
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.35fr_1fr]">
          <motion.div variants={itemVariants}>
            <Card className="h-full space-y-5 rounded-xl">
              <h2 className="text-h2 font-semibold text-text-main">How The Product Delivers Value</h2>
              <p className="text-body leading-6 text-text-muted">
                Incrypt is designed as a complete product, not just a notes screen. Each area of the experience supports secure collaboration, operational clarity, and scalable daily usage.
              </p>

              <div className="space-y-3">
                <div className="rounded-lg border border-border-subtle bg-bg-subtle p-4">
                  <h3 className="text-body font-semibold text-text-main">Secure Authentication & User Isolation</h3>
                  <p className="mt-1 text-[13px] text-text-muted">
                    Notes and actions are scoped to authenticated users to preserve privacy and data ownership.
                  </p>
                </div>
                <div className="rounded-lg border border-border-subtle bg-bg-subtle p-4">
                  <h3 className="text-body font-semibold text-text-main">Complete Note Lifecycle</h3>
                  <p className="mt-1 text-[13px] text-text-muted">
                    Teams can create, update, review, and maintain critical notes in one streamlined workflow.
                  </p>
                </div>
                <div className="rounded-lg border border-border-subtle bg-bg-subtle p-4">
                  <h3 className="text-body font-semibold text-text-main">Admin Visibility & Governance</h3>
                  <p className="mt-1 text-[13px] text-text-muted">
                    Administrative views and audit logs provide transparency that supports trust and accountability.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-col gap-6">
            <Card className="w-full rounded-xl border-primary/15 bg-gradient-to-b from-bg-surface to-bg-subtle px-5 py-5">
              <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-white">
                <FiShield className="text-xl" />
              </div>
              <h2 className="text-h3 font-semibold text-text-main">Product Principles</h2>
              <div className="mt-4 space-y-3">
                {productPrinciples.map((principle) => {
                  const Icon = principle.icon;
                  return (
                    <div
                      key={principle.title}
                      className="rounded-md border border-border-subtle bg-bg-surface px-3 py-3"
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="text-primary" />
                        <h3 className="text-[13px] font-semibold text-text-main">
                          {principle.title}
                        </h3>
                      </div>
                      <p className="mt-1 text-[12px] leading-5 text-text-muted">{principle.text}</p>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="w-full space-y-3 rounded-xl">
              <h2 className="text-h3 font-semibold text-text-main">Stay Connected</h2>
              <p className="text-body text-text-main">
                Follow product releases, security updates, and roadmap improvements.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noreferrer"
                  className="group flex h-10 w-10 items-center justify-center rounded-full bg-primary/90 text-white transition-all duration-200 hover:scale-110 hover:bg-primary hover:shadow-md"
                >
                  <FaFacebookF
                    size={18}
                    className="transition-transform duration-200 group-hover:scale-110"
                  />
                </a>

                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noreferrer"
                  className="group flex h-10 w-10 items-center justify-center rounded-full bg-primary/90 text-white transition-all duration-200 hover:scale-110 hover:bg-primary hover:shadow-md"
                >
                  <FaTwitter
                    size={18}
                    className="transition-transform duration-200 group-hover:scale-110"
                  />
                </a>

                <a
                  href="https://linkedin.com/in/bengj10"
                  target="_blank"
                  rel="noreferrer"
                  className="group flex h-10 w-10 items-center justify-center rounded-full bg-primary/90 text-white transition-all duration-200 hover:scale-110 hover:bg-primary hover:shadow-md"
                >
                  <FaLinkedinIn
                    size={18}
                    className="transition-transform duration-200 group-hover:scale-110"
                  />
                </a>

                <a
                  href="https://instagram.com/bengj10"
                  target="_blank"
                  rel="noreferrer"
                  className="group flex h-10 w-10 items-center justify-center rounded-full bg-primary/90 text-white transition-all duration-200 hover:scale-110 hover:bg-primary hover:shadow-md"
                >
                  <FaInstagram
                    size={18}
                    className="transition-transform duration-200 group-hover:scale-110"
                  />
                </a>
              </div>
            </Card>
          </motion.div>
        </div>

        <motion.div variants={itemVariants}>
          <Card className="rounded-xl border-primary/20 bg-gradient-to-r from-primary to-primary-hover px-6 py-6 text-white sm:px-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="max-w-2xl">
                <h2 className="text-[24px] font-semibold leading-tight">
                  Built for modern teams that need secure, dependable note operations.
                </h2>
                <p className="mt-2 text-[14px] text-white/90">
                  Incrypt combines practical workflows with enterprise-minded protection so your team can focus on decisions, not tooling complexity.
                </p>
              </div>

              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-primary transition-all duration-150 hover:bg-slate-100"
              >
                Create your account
                <FiArrowRight />
              </Link>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AboutPage;
